/**
 * PURPOSE: Mostrar y administrar instituciones participantes agrupadas por región.
 * RESPONSIBILITIES: Listar participantes, conservar grupos configurados y mostrar el grupo de respaldo sin perder instituciones.
 * COLLABORATORS: getparticipants, ModalEditInstitution, ModalEditUniform y FullScreenEliminatoria.
 * ROUTE: frontend/src/components/FullScreenModal/FullScreenGrupos/Instituciones/Regionales/Regionales.jsx
 */

// src/components/FullScreenModal/FullScreenGrupos/Instituciones/Regionales/Regionales.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { getParticipantFullName } from "../../../../../utils/participantDisplayName.js";
import API_BASE_URL from "../../../../../config/config.js";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import ModalEditInstitution from "../../../../Modals/ModalEditInstitution/ModalEditInstitution";
import ModalEditUniform from "../../../../Modals/ModalEditUniform/ModalEditUniform";
import { UniformMatrix } from "../../../../UniformMatrix/UniformMatrix.jsx";
import { useSisdeuImportStatus } from "../../../../../hooks/useSisdeuImportStatus.js";

const Regionales = ({ config }) => {
  const { idevent, idsport } = useParams();
  // FTR-0048: con la importación SISDEU nueva activa para el evento (company 1) se oculta el sync legacy.
  const { status: sisdeuImportStatus } = useSisdeuImportStatus({ idevent, idsport });
  const eventId = Number(idevent);
  const [participantes, setParticipantes] = useState([]);
  const [regionMap, setRegionMap] = useState({});
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [posicion, setPosicion] = useState("");
  const [tag, setTag] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalUniformOpen, setIsModalUniformOpen] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  // ->
  const [posicionDirigida, setPosicionDirigida] = useState("");
  const [grupoDirigido, setGrupoDirigido] = useState("");

  useEffect(() => {
    if (config && config.separacionData) {
      setRegionMap(config.separacionData);
    }
  }, [config]);

  useEffect(() => {
    const getParticipantes = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/getparticipants`, {
          idevent: Number(idevent),
          idsport: Number(idsport),
          idtypephase: 1,
        });
        setParticipantes(response.data);
      } catch (error) {
        console.error("Error fetching participants:", error);
      }
    };
    if (idevent && idsport) {
      getParticipantes();
    }
  }, [idevent, idsport]);

  const regiones = {};

  participantes.forEach((participant) => {
    const regionInfo = regionMap[participant.idseparacion];
    if (regionInfo) {
      if (!regiones[regionInfo.name]) {
        regiones[regionInfo.name] = [];
      }
      regiones[regionInfo.name].push({
        ...participant,
        img_path: regionInfo.img_path,
        abrev: regionInfo.abrev,
      });
    } else {
      if (!regiones["INSTITUCIONES PARTICIPANTES"]) {
        regiones["INSTITUCIONES PARTICIPANTES"] = [];
      }
      regiones["INSTITUCIONES PARTICIPANTES"].push(participant);
    }
  });

  // Ordenar los participantes dentro de cada región según su atributo "posicion"
  Object.keys(regiones).forEach((region) => {
    regiones[region].sort((a, b) => {
      if (a.posicion === null || a.posicion === undefined) return 1;
      if (b.posicion === null || b.posicion === undefined) return -1;
      return a.posicion - b.posicion;
    });
  });

  const configuredRegionNames = Object.keys(regionMap).map(
    (key) => regionMap[key].name
  );

  // Conserva el orden configurado y agrega al final cualquier grupo de respaldo.
  // Esto permite mostrar "INSTITUCIONES PARTICIPANTES" cuando los registros no
  // tienen idseparacion o cuando la configuración regional está vacía.
  const fallbackRegionNames = Object.keys(regiones).filter(
    (regionName) => !configuredRegionNames.includes(regionName)
  );

  const orderedRegions = [
    ...configuredRegionNames,
    ...fallbackRegionNames,
  ];

  const nonEmptyRegions = orderedRegions.filter(
    (region) => regiones[region] && regiones[region].length > 0
  );

  const isSingleRegion = nonEmptyRegions.length === 1;

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleRowClick = (participant, isFirst) => {
    setSelectedParticipant(participant);
    setPosicion(participant.posicion || "");
    setTag(participant.tag || "");
    setPosicionDirigida(participant.posicionDirigida || "");
    setGrupoDirigido(participant.grupoDirigido || "");

    if (isFirst === 1) {
      // Click en el LOGO -> ModalEditInstitution
      setIsModalUniformOpen(false);
      setIsModalOpen(true);
    } else {
      // Click en la FILA (no logo) -> ModalEditUniform
      setIsModalOpen(false);
      setIsModalUniformOpen(true);
    }
  };


  const handleModalClick = (e) => {
    if (e.target.classList.contains("modal-container")) {
      setIsModalOpen(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.post(`${API_BASE_URL}/deleteparticipant`, {
        idevent: selectedParticipant.idevent,
        idsport: selectedParticipant.idsport,
        idtypephase: selectedParticipant.idtypephase,
        idinstitution: selectedParticipant.idinstitution,
      })
      setIsModalOpen(false);
      const response = await axios.post(`${API_BASE_URL}/getparticipants`, {
        idevent: Number(idevent),
        idsport: Number(idsport),
        idtypephase: 1,
      })
      setParticipantes(response.data);
      toast.success("¡Participante Eliminado Correctamente!");
    } catch (error) {
      toast.error("¡Error al Eliminar al Participante!");
      console.error("Error delete participant:", error);
    }
  }
  const handleUpdate = async () => {
    try {
      await axios.patch(`${API_BASE_URL}/updateposiciontag`, {
        idevent: selectedParticipant.idevent,
        idsport: selectedParticipant.idsport,
        idtypephase: selectedParticipant.idtypephase,
        idinstitution: selectedParticipant.idinstitution,
        posicion: posicion,
        tag: tag,
        //
        posicionDirigida: posicionDirigida,
        grupoDirigido: grupoDirigido,
      });
      setIsModalOpen(false);
      const response = await axios.post(`${API_BASE_URL}/getparticipants`, {
        idevent: Number(idevent),
        idsport: Number(idsport),
        idtypephase: 1,
      });
      setParticipantes(response.data);
      toast.success("¡Participante Actualizado Correctamente!");
    } catch (error) {
      toast.error("¡Error al Actualizar al Participante!");
      console.error("Error updating participant:", error);
    }
  };

  const handleUpdateAcreditados = async () => {
    try {
      let iduni
      if (Number(idevent) === 1 || Number(idevent) === 150) {
        iduni = selectedParticipant.Institution.idaux ? selectedParticipant.Institution.idaux : selectedParticipant.idinstitution
      } else {
        iduni = selectedParticipant.idinstitution
      }
      console.log("iduni", iduni)
      const response = await axios.post(
        `${API_BASE_URL}/actualizaracreditados`,
        {
          idevent: selectedParticipant.idevent,
          idsport: selectedParticipant.idsport,
          idinstitution: iduni,
          idinst: selectedParticipant.idinstitution
          // idinstitution: selectedParticipant.idinstitution,
        }
      );
      toast.success("¡Acreditados Actualizados!");
    } catch (error) {
      toast.error("¡Error al actualizar los Acreditados!");
      console.error("Error updating acreditados:", error);
    }
  };

  const viewToggle = (
    <div className="col-span-full mb-1 flex justify-end">
      <div className="inline-flex rounded-xl bg-gray-100 p-1">
        <button type="button" onClick={() => setViewMode("list")} className={`rounded-lg px-4 py-2 text-sm font-bold transition ${viewMode === "list" ? "bg-white text-gray-900 shadow" : "text-gray-500"}`}>Lista</button>
        <button type="button" onClick={() => setViewMode("uniforms")} className={`rounded-lg px-4 py-2 text-sm font-bold transition ${viewMode === "uniforms" ? "bg-white text-indigo-700 shadow" : "text-gray-500"}`}>Uniformes</button>
      </div>
    </div>
  );

  if (viewMode === "uniforms") {
    return (
      <div className="p-4">
        {viewToggle}
        <UniformMatrix idevent={Number(idevent)} idsport={Number(idsport)} />
      </div>
    );
  }

  // Si es el evento 16, dividimos la lista total de participantes en dos tablas
  if (eventId === 16) {
    const allParticipants = nonEmptyRegions.flatMap(region => regiones[region]);
    const mid = Math.ceil(allParticipants.length / 2);
    const firstHalf = allParticipants.slice(0, mid);
    const secondHalf = allParticipants.slice(mid);

    return (
      <div className="p-4 grid gap-4 grid-cols-1 md:grid-cols-2">
        {viewToggle}
        {[firstHalf, secondHalf].map((group, idx) => (
          <div
            key={idx}
            className="bg-white rounded-lg shadow-md p-4 overflow-x-auto"
          >
            <table className="w-full min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Participante
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Condición
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Sigla
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {group.map((participant, index) => (
                  <tr
                    key={index}
                    onClick={() => handleRowClick(participant, 2)}
                    className="cursor-pointer"
                  >
                    <td className="px-4 py-2 whitespace-nowrap flex items-center">
                      <img
                        src={`${participant.Institution.path_base}${participant.Institution.image_path}`}
                        alt="Logo"
                        className="w-8 h-8 rounded-full object-cover mr-2"
                        onClick={(e) => { e.stopPropagation(); handleRowClick(participant, 1); }}
                      />
                      {getParticipantFullName(participant)}
                      {config?.useCabezaGrupo && participant?.posicion && (
                        <span className="ml-1">({participant.posicion})</span>
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {participant.tag || 'N/A'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {participant.Institution.abrev}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {/* Modal compartido */}
        {
          isModalOpen && (
            <ModalEditInstitution
              isModalOpen={isModalOpen}
              handleModalClose={handleModalClose}
              posicion={posicion}
              setPosicion={setPosicion}
              tag={tag}
              setTag={setTag}
              handleUpdate={handleUpdate}
              handleDelete={handleDelete}
              handleUpdateAcreditados={handleUpdateAcreditados}
              showUpdateAcreditados={!sisdeuImportStatus.integrationActive}
              posicionDirigida={posicionDirigida}
              setPosicionDirigida={setPosicionDirigida}
              grupoDirigido={grupoDirigido}
              setGrupoDirigido={setGrupoDirigido}
              config={config}
            />
          )
        }

        {
          isModalUniformOpen && (
            <ModalEditUniform open={isModalUniformOpen} setOpen={setIsModalUniformOpen} />
          )
        }
      </div>
    );
  }

  // Para otros eventos, mostrar la tabla por regiones como antes
  return (
    <div
      className={`p-4 grid gap-4 ${nonEmptyRegions.length === 1
        ? 'grid-cols-1 justify-center'
        : 'grid-cols-1 md:grid-cols-2'}
      `}
    >
      {viewToggle}
      {nonEmptyRegions.map((region) => (
        <div
          key={region}
          className={`bg-white rounded-lg shadow-md p-4 overflow-x-auto ${nonEmptyRegions.length === 1 ? 'mx-auto max-w-4xl' : 'max-w-4xl'
            }`}
        >
          <h2 className="text-center text-xl font-bold mb-2">{region}</h2>
          <table className="w-full min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                  Participante
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                  Condición
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                  Sigla
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {regiones[region].map((participant, index) => (
                <tr
                  key={index}
                  onClick={() => handleRowClick(participant, 2)}
                  className="cursor-pointer"
                >
                  <td className="px-4 py-2 whitespace-nowrap flex items-center">
                    <img
                      src={`${participant.Institution.path_base}${participant.Institution.image_path}`}
                      alt="Logo"
                      className="w-8 h-8 rounded-full object-cover mr-2 hover:bg-gray-50"
                      onClick={(e) => { e.stopPropagation(); handleRowClick(participant, 1); }}
                    />
                    {getParticipantFullName(participant)}
                    {config?.useCabezaGrupo && participant?.posicion && (
                      <span className="ml-1">({participant.posicion})</span>
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {participant.tag || 'N/A'}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {participant.Institution.abrev}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Modal compartido */}
      <ModalEditInstitution
        isModalOpen={isModalOpen}
        handleModalClose={handleModalClose}
        posicion={posicion}
        setPosicion={setPosicion}
        tag={tag}
        setTag={setTag}
        handleUpdate={handleUpdate}
        handleDelete={handleDelete}
        handleUpdateAcreditados={handleUpdateAcreditados}
        showUpdateAcreditados={!sisdeuImportStatus.integrationActive}
        posicionDirigida={posicionDirigida}
        setPosicionDirigida={setPosicionDirigida}
        grupoDirigido={grupoDirigido}
        setGrupoDirigido={setGrupoDirigido}
        config={config}
      />

      {isModalUniformOpen && (
        <ModalEditUniform
          open={isModalUniformOpen}
          setOpen={setIsModalUniformOpen}
          participant={selectedParticipant}
        />
      )}
    </div>
  );
};

export default Regionales;
